/**
 * Eviction notices pre-answer nothing (RAC-10343).
 *
 * RocketLawyer returns every radio page with an option already true — on New
 * York's reason page, "The tenant has failed to pay rent". On a lease that is
 * harmless; on an eviction notice the reason is not a preference. It selects
 * which instrument is produced (a rent demand vs. a notice to terminate), which
 * statute is cited, and how many days the tenant gets. A landlord ending a
 * month-to-month tenancy who clicks past that page serves a rent demand for
 * money nobody owes — and a defective notice restarts their whole case.
 *
 * So the wizard blanks radio defaults on an eviction and refuses to advance
 * until the landlord actually picks. This mirrors the prefill, which likewise
 * refuses to TRANSMIT a radio.
 */

/**
 * Whether this question's radios should ignore whatever RocketLawyer
 * pre-selected.
 *
 * The subtlety this exists for: RocketLawyer returns the landlord's stored
 * answer and its own untouched default in exactly the same shape, so blanking
 * unconditionally would wipe a real choice every time the page was re-served,
 * and blanking never would let the default through as though it had been
 * chosen. Nothing on the page tells them apart. Only a record of what the
 * landlord sent us does, and that is chosen_field_ids.
 *
 * Keyed per QUESTION, on whether any of ITS radios has ever been clicked —
 * not on whether the page was submitted. A page-level test reopens the hole
 * from the other side: Save & Continue Later and Back both submit without
 * requiring an answer, so the page can be marked submitted with every radio
 * still false, which is precisely how RocketLawyer's default gets restored.
 * The next serve would then adopt that default, satisfy the guard below with
 * it, and hand the landlord a notice they never picked.
 *
 * completed_steps is deliberately NOT what this reads, though the preview gate
 * still does: revealing the draft is a stronger claim than trusting a stored
 * answer, and only Next means the landlord committed to a reason rather than
 * parking it.
 *
 * @param {Object}          question         a normalized page question
 * @param {Array|Set|null}  chosenFieldIds   interview.chosen_field_ids
 */
export function shouldSuppressRadioDefaults({ isEviction, question, chosenFieldIds }) {
    if (!isEviction) {
        return false;
    }

    const radios = (question?.fields || []).filter((field) => field?.type === 'RADIO');

    if (radios.length === 0) {
        return false;
    }

    const chosen = chosenFieldIds instanceof Set
        ? chosenFieldIds
        : new Set(Array.isArray(chosenFieldIds) ? chosenFieldIds : []);

    return !radios.some((field) => chosen.has(field.id));
}

/**
 * The first question on the page carrying an unanswered radio, or null.
 *
 * Scoped to ANY question containing a RADIO, not only questions where every
 * field is one. That is deliberate, and it is the scope that keeps this
 * coherent with shouldSuppressRadioDefaults() above: suppression blanks radios
 * FIELD by field, so a radio sitting beside a text input gets blanked like any
 * other. Guarding only all-radio questions would leave exactly those radios
 * blanked and unguarded — submitted with every option false, which is precisely
 * how RocketLawyer's own default gets restored (Brad's review of RAC-10343).
 *
 * The invariant is simply: whatever we blank, we require the landlord to
 * answer. Matching the wizard's all-radio RENDERING test instead would tie a
 * legal guarantee to a layout decision, and the two have no reason to agree.
 *
 * Whether any of the 51 eviction templates actually presents a radio inside a
 * mixed question is not something this can wait on — the interviews branch on
 * their own answers, so no walk enumerates every page a landlord might reach.
 * Holding the invariant costs nothing and removes the question.
 *
 * The wizard draws radio controls in BOTH layouts — the all-radio question and
 * the mixed one — so every field this guard requires an answer for has a
 * control that can give it one. It did not always: a mixed-question radio was
 * once blanked here and drawn as a text box, which no landlord could ever
 * satisfy, dead-ending the interview on that page.
 *
 * @param {Array} questions visible questions for the current page
 * @param {Object} answers  formAnswers, keyed by field id
 */
export function firstUnansweredRadioQuestion(questions, answers) {
    for (const question of questions || []) {
        const radios = (question?.fields || []).filter((field) => field?.type === 'RADIO');

        if (radios.length === 0) {
            continue;
        }

        if (!radios.some((field) => answers?.[field.id] === true)) {
            return question;
        }
    }

    return null;
}
